import * as React from 'react'

import { cn } from '@/lib/utils'
import { useKeyboardHeight } from '@/lib/keyboard'

export type SnapPoint = number | string

interface ResizableDrawerProps {
  /** Resting heights for the drawer. Numbers are viewport-height fractions
   *  (0..1); strings can be "300px" or "50%". */
  snapPoints: SnapPoint[]
  /** Controlled active snap point. Omit to use uncontrolled mode. */
  activeSnapPoint?: SnapPoint
  onActiveSnapPointChange?: (snap: SnapPoint) => void
  /** Initial snap point for uncontrolled mode. Defaults to snapPoints[0]. */
  defaultSnapPoint?: SnapPoint
  /** Hug the content's natural height (capped at max-h) instead of snapping to
   *  a fixed fraction. Disables drag-resize and the grabber. Use for short
   *  content that a fixed snap would either gap below or clip. */
  fitContent?: boolean
  /** Show the grabber handle at the top of the drawer. Defaults to true. */
  handle?: boolean
  /** Render a dimmed backdrop behind the panel and add an elevation shadow.
   *  Opt-in (default false) so backdrop-less "peek" usages stay unchanged. */
  overlay?: boolean
  /** Controlled visibility. When provided, the drawer slides in/out and
   *  unmounts itself after the exit animation. Omit for the always-visible
   *  peek mode (legacy behavior, e.g. the home screen flight drawer). */
  open?: boolean
  /** Called when the backdrop is tapped (only relevant when `overlay`). */
  onOverlayClick?: () => void
  /** Extra classes applied to the panel. */
  className?: string
  children: React.ReactNode
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

function resolveSnap(snap: SnapPoint, viewportHeight: number): number {
  if (typeof snap === 'number') return snap * viewportHeight
  if (snap.endsWith('px')) return parseFloat(snap)
  if (snap.endsWith('%')) return (parseFloat(snap) / 100) * viewportHeight
  const n = parseFloat(snap)
  return Number.isFinite(n) ? n : 0
}

const MOVE_THRESHOLD = 4
const VELOCITY_THRESHOLD = 0.5
const VELOCITY_WINDOW_MS = 100
const MAX_SNAP_EPSILON = 1

type GestureMode = 'idle' | 'pending' | 'resize' | 'scroll'

export function ResizableDrawer({
  snapPoints,
  activeSnapPoint,
  onActiveSnapPointChange,
  defaultSnapPoint,
  fitContent = false,
  handle = true,
  overlay = false,
  onOverlayClick,
  open,
  className,
  children,
}: ResizableDrawerProps) {
  const managed = open !== undefined
  const [internalSnap, setInternalSnap] = React.useState<SnapPoint>(
    defaultSnapPoint ?? snapPoints[0],
  )
  const active = activeSnapPoint ?? internalSnap
  const setActive = (next: SnapPoint) => {
    if (activeSnapPoint === undefined) setInternalSnap(next)
    onActiveSnapPointChange?.(next)
  }

  const [vh, setVh] = React.useState(() =>
    typeof window === 'undefined' ? 0 : window.innerHeight,
  )
  // With Keyboard.resize: "none" the webview frame is stable, so this only
  // fires on rotation — the keyboard is handled via the lift below instead.
  React.useEffect(() => {
    const onResize = () => setVh(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const keyboardHeight = useKeyboardHeight()

  const targetHeight = resolveSnap(active, vh)

  const snapHeights = React.useMemo(
    () => snapPoints.map((s) => resolveSnap(s, vh)),
    [snapPoints, vh],
  )
  const maxSnapHeight = React.useMemo(
    () => (snapHeights.length ? Math.max(...snapHeights) : 0),
    [snapHeights],
  )

  const dragRef = React.useRef<{
    startY: number
    startX: number
    startHeight: number
    startScrollTop: number
  } | null>(null)
  const modeRef = React.useRef<GestureMode>('idle')
  const samplesRef = React.useRef<{ t: number; y: number }[]>([])
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [draggingHeight, setDraggingHeight] = React.useState<number | null>(
    null,
  )

  const pushSample = (y: number) => {
    const now = performance.now()
    samplesRef.current.push({ t: now, y })
    const cutoff = now - VELOCITY_WINDOW_MS
    while (samplesRef.current.length && samplesRef.current[0].t < cutoff) {
      samplesRef.current.shift()
    }
  }

  const computeVelocity = () => {
    const s = samplesRef.current
    if (s.length < 2) return 0
    const first = s[0]
    const last = s[s.length - 1]
    const dt = last.t - first.t
    if (dt <= 0) return 0
    return (last.y - first.y) / dt
  }

  const beginResize = (startY: number, startHeight: number) => {
    dragRef.current = {
      startY,
      startX: 0,
      startHeight,
      startScrollTop: 0,
    }
    samplesRef.current = []
    pushSample(startY)
    setDraggingHeight(startHeight)
  }

  const endResize = (finalHeight: number, velocity: number) => {
    if (!snapPoints.length) {
      dragRef.current = null
      samplesRef.current = []
      setDraggingHeight(null)
      return
    }

    let currentIdx = 0
    let currentDist = Infinity
    for (let i = 0; i < snapHeights.length; i++) {
      const d = Math.abs(snapHeights[i] - targetHeight)
      if (d < currentDist) {
        currentDist = d
        currentIdx = i
      }
    }

    const sorted = snapHeights
      .map((h, i) => ({ h, i }))
      .sort((a, b) => a.h - b.h)
    const orderIdx = sorted.findIndex((s) => s.i === currentIdx)

    let nextIdx = currentIdx
    if (velocity < -VELOCITY_THRESHOLD) {
      const stepped = sorted[Math.min(orderIdx + 1, sorted.length - 1)]
      nextIdx = stepped.i
    } else if (velocity > VELOCITY_THRESHOLD) {
      const stepped = sorted[Math.max(orderIdx - 1, 0)]
      nextIdx = stepped.i
    } else {
      let bestIdx = 0
      let bestDist = Infinity
      for (let i = 0; i < snapHeights.length; i++) {
        const d = Math.abs(snapHeights[i] - finalHeight)
        if (d < bestDist) {
          bestDist = d
          bestIdx = i
        }
      }
      nextIdx = bestIdx
    }

    dragRef.current = null
    samplesRef.current = []
    setDraggingHeight(null)
    setActive(snapPoints[nextIdx])
  }

  // --- Content (resize or scroll, decided on first move) ---

  const handleContentPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Fit-content mode is not drag-resizable; let the content scroll natively.
    if (fitContent) return
    const el = contentRef.current
    if (!el) return
    // Portaled React descendants (e.g. a Drawer opened by a card inside this
    // drawer) bubble synthetic events here even though their DOM lives on
    // document.body — never treat those as a resize gesture.
    if (!(e.target instanceof Node) || !el.contains(e.target)) return
    // Ignore non-primary pointers (e.g. multitouch pinch).
    if (e.isPrimary === false) return
    dragRef.current = {
      startY: e.clientY,
      startX: e.clientX,
      startHeight: targetHeight,
      startScrollTop: el.scrollTop,
    }
    samplesRef.current = []
    modeRef.current = 'pending'
  }

  const handleContentPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    const dy = e.clientY - dragRef.current.startY
    const dx = e.clientX - dragRef.current.startX

    if (modeRef.current === 'pending') {
      if (Math.abs(dy) < MOVE_THRESHOLD) return
      if (Math.abs(dx) > Math.abs(dy)) {
        modeRef.current = 'scroll'
        return
      }
      const startScrollTop = dragRef.current.startScrollTop
      if (startScrollTop > 0) {
        modeRef.current = 'scroll'
        return
      }
      if (dy > 0) {
        // Finger moved down at scrollTop=0 → shrink.
        modeRef.current = 'resize'
      } else {
        // Finger moved up at scrollTop=0 → expand unless already at max.
        if (targetHeight >= maxSnapHeight - MAX_SNAP_EPSILON) {
          modeRef.current = 'scroll'
          return
        }
        modeRef.current = 'resize'
      }
      e.currentTarget.setPointerCapture(e.pointerId)
      beginResize(dragRef.current.startY, dragRef.current.startHeight)
    }

    if (modeRef.current !== 'resize' || !dragRef.current) return
    pushSample(e.clientY)
    setDraggingHeight(clamp(dragRef.current.startHeight - dy, 0, vh))
  }

  const handleContentPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const wasResizing = modeRef.current === 'resize' && dragRef.current !== null
    if (wasResizing) {
      const finalHeight = draggingHeight ?? targetHeight
      const velocity = computeVelocity()
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        // not captured
      }
      modeRef.current = 'idle'
      endResize(finalHeight, velocity)
    } else {
      dragRef.current = null
      samplesRef.current = []
      modeRef.current = 'idle'
    }
  }

  // iOS WebKit needs a non-passive touchmove listener to actually suppress
  // native scrolling while we're driving the drawer height ourselves.
  React.useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const onTouchMove = (ev: TouchEvent) => {
      if (modeRef.current === 'resize') ev.preventDefault()
    }
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [])

  // Whenever the committed snap isn't the max, keep scroll pinned at the top
  // so the next upward drag is unambiguously a resize.
  React.useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return
    if (targetHeight < maxSnapHeight - MAX_SNAP_EPSILON) {
      el.scrollTop = 0
    }
  }, [targetHeight, maxSnapHeight])

  // Enter/exit animation for managed (open-controlled) mode, mirroring
  // ui/drawer.tsx: mount, then flip visible next frame so the slide-in
  // transitions; on close, slide out and unmount after the duration.
  const [mounted, setMounted] = React.useState(managed ? !!open : true)
  const [visible, setVisible] = React.useState(!managed)

  React.useEffect(() => {
    if (!managed) return
    if (open) {
      setMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })
    } else {
      setVisible(false)
      const id = setTimeout(() => setMounted(false), 200) // match duration
      return () => clearTimeout(id)
    }
  }, [open, managed])

  // Freeze the last open-state children (and the props that shape the panel)
  // during the exit animation, so parent state resets in onClose (e.g.
  // clearing the selected flight, which flips FlightDrawer to create mode)
  // don't swap the content or resize the panel mid-slide.
  const lastRenderRef = React.useRef({ children, fitContent, overlay })
  if (!managed || open) lastRenderRef.current = { children, fitContent, overlay }
  const frozen = lastRenderRef.current

  const isDragging = draggingHeight !== null
  // In fit-content mode the panel hugs its children (height auto), capped by the
  // panel's max-h. Otherwise it's driven by the active/dragged snap height —
  // clamped to the strip above the keyboard while it's up, since the panel is
  // lifted to sit on top of it.
  const visibleVh = vh - keyboardHeight
  const height = frozen.fitContent
    ? undefined
    : isDragging
      ? draggingHeight!
      : keyboardHeight > 0
        ? Math.min(targetHeight, visibleVh)
        : targetHeight

  if (!mounted) return null

  return (
    <>
      {frozen.overlay && (
        // Sit above the fixed top bar (z-50) and bottom nav (z-55) so the dim
        // covers the whole screen, while staying below the picker drawers
        // (z-60) this sheet can launch.
        <div
          className={cn(
            'fixed inset-0 z-[56] bg-black/50 transition-opacity duration-200 ease-out',
            managed && (visible ? 'opacity-100' : 'opacity-0 pointer-events-none'),
          )}
          onClick={onOverlayClick}
        />
      )}
    <div
      className={cn(
        'app-frame drawer-max-h fixed inset-x-0 bottom-0 flex pt-2 flex-col rounded-t-[20px] border bg-surface-L0 outline-none',
        // In modal (overlay) mode the panel rides just above its backdrop but
        // below the z-60 pickers; the backdrop-less peek mode keeps z-50.
        frozen.overlay ? 'z-[58]' : 'z-50',
        frozen.overlay && 'shadow-xl',
        !isDragging && 'transition-[height,bottom,max-height,translate] duration-200 ease-out',
        managed && (visible ? 'translate-y-0' : 'translate-y-full pointer-events-none'),
        className,
      )}
      style={{
        height,
        ...(keyboardHeight > 0
          ? { bottom: keyboardHeight, maxHeight: visibleVh }
          : undefined),
      }}
    >
      {handle && !frozen.fitContent && (
        <div className="mx-auto mb-2 h-1.5 w-10 shrink-0 rounded-full bg-surface-L2" />
      )}
      <div
        ref={contentRef}
        onPointerDown={handleContentPointerDown}
        onPointerMove={handleContentPointerMove}
        onPointerUp={handleContentPointerEnd}
        onPointerCancel={handleContentPointerEnd}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {frozen.children}
      </div>
    </div>
    </>
  )
}
